import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FiShare2, FiPlus, FiEye, FiLoader } from 'react-icons/fi';
import moment from 'moment';
import api from '../../../../api/api';
import { XAccountResult, LinkedInAccountResult, LinkedInCompany } from '../../../../model/SocialAccountsResult';
import Toggle from '../../Toggle';
import { toast } from '../../Toast';
import { useClient } from '../clientSettingsData';
import { SocialAccount, SAMPLE_POST } from './announcementData';
import { SectionProps } from './AnnouncementSettings';

interface Props extends SectionProps {
    accounts: SocialAccount[];
    setAccounts: React.Dispatch<React.SetStateAction<SocialAccount[]>>;
}

const errorText = (e: any, fallback: string) => typeof e?.response?.data === 'string' && e.response.data ? e.response.data : fallback;

const SETTINGS: { key: 'priceSensitive' | 'afterApproval' | 'askRoutine' | 'picture'; title: string; hint: string }[] = [
    { key: 'priceSensitive', title: 'Post price‑sensitive announcements automatically', hint: 'Goes out at the same moment as the email' },
    { key: 'afterApproval', title: 'Post other announcements after approval', hint: 'The approver ticks “also post” when they approve' },
    { key: 'askRoutine', title: 'Ask before posting routine lodgements', hint: 'Appendix 3B, quotation notices and similar' },
    { key: 'picture', title: 'Include the announcement picture', hint: 'Falls back to your logo if the PDF has no image' },
];

const SocialPosts: React.FC<Props> = ({ form, setForm, accounts, setAccounts }) => {
    const { stock, name, ticker } = useClient();
    const wordingRef = useRef<HTMLTextAreaElement>(null);

    const site = (stock?.websiteUrl || '49metals.com.au').replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    // click-to-insert fields; each is replaced with the real value when Investi posts (sample values in the preview)
    const fields = [
        { token: '{{announcement_headline}}', label: 'Announcement headline', sample: SAMPLE_POST.title },
        { token: '{{announcement_link}}', label: 'Announcement link', sample: `${site}/asx` },
        { token: '{{announcement_date}}', label: 'Announcement date', sample: moment().format('D MMM YYYY') },
        { token: '{{company_name}}', label: 'Company name', sample: name },
        { token: '{{ticker}}', label: 'Ticker', sample: ticker },
    ];

    const setWording = (wording: string) => setForm(f => ({ ...f, social: { ...f.social, wording } }));

    // puts the field where the cursor is (or at the end), then puts the cursor after it
    const insert = (token: string) => {
        const el = wordingRef.current;
        const text = form.social.wording;
        const start = el ? el.selectionStart : text.length;
        const end = el ? el.selectionEnd : text.length;
        const next = text.slice(0, start) + token + text.slice(end);
        setWording(next);
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + token.length, start + token.length);
        });
    };

    const preview = fields
        .reduce((text, x) => text.split(x.token).join(x.sample), form.social.wording)
        .replace(/\s*\n+\s*/g, ' ')
        .trim();

    const showX = (x: XAccountResult | null) => setAccounts(list => list.map(a => a.id !== 'x' ? a
        : { ...a, connected: !!x, detail: x ? `@${x.username} · connected by ${x.connectedBy}` : 'Not connected' }));

    // companies to choose from while the person who connected LinkedIn admins several
    const [linkedInCompanies, setLinkedInCompanies] = useState<LinkedInCompany[] | null>(null);
    const showLinkedIn = (l: LinkedInAccountResult | null) => {
        setLinkedInCompanies(l && !l.companyName ? l.companies : null);
        const detail = !l ? 'Not connected'
            : !l.companyName ? 'Choose the company to post as'
            : `${l.companyName} · reconnect by ${moment(l.reconnectByUtc).format('D MMM YYYY')}`;
        setAccounts(list => list.map(a => a.id !== 'linkedin' ? a : { ...a, connected: !!l?.companyName, detail }));
    };

    // X and LinkedIn send the browser back to .../social/{x|linkedin}-callback?code&state (or ?error) after their sign-in page
    const history = useHistory();
    const location = useLocation();
    const stockId = stock?.id;
    // the LinkedIn callback takes a few seconds (tokens, then the companies), so show it's working straight away
    const [linkedInLoading, setLinkedInLoading] = useState(() => location.pathname.endsWith('/linkedin-callback'));
    // nothing on the cards until the server says what's connected, so Connect doesn't flash up
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        if (!stockId) return;
        const load = async () => {
            const params = new URLSearchParams(location.search);
            const code = params.get('code');
            const state = params.get('state');
            if (location.pathname.endsWith('/x-callback')) {
                history.replace('/client-settings/announcements/social');
                if (!code || !state) {
                    toast.error('X wasn’t connected');
                } else {
                    try {
                        const x = await api().postXCallback(code, state);
                        showX(x);
                        toast.info(`Connected X as @${x.username}`);
                    } catch (e) {
                        toast.error(errorText(e, 'X wasn’t connected'));
                    }
                }
            }
            if (location.pathname.endsWith('/linkedin-callback')) {
                history.replace('/client-settings/announcements/social');
                if (!code || !state) {
                    toast.error('LinkedIn wasn’t connected');
                } else {
                    try {
                        const l = await api().postLinkedInCallback(code, state);
                        toast.info(l.companyName ? `Connected LinkedIn to ${l.companyName}` : 'LinkedIn connected, now choose the company to post as');
                    } catch (e) {
                        toast.error(errorText(e, 'LinkedIn wasn’t connected'));
                    }
                }
            }
            const result = await api().getSocialAccounts(stockId);
            showX(result.x);
            showLinkedIn(result.linkedIn);
        };
        load()
            .catch(() => toast.error('Couldn’t load the connected social accounts'))
            .finally(() => { setLinkedInLoading(false); setLoaded(true); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stockId]);

    const connect = async (id: string) => {
        if (!stockId) return;
        const name = accounts.find(a => a.id === id)?.name;
        try {
            const url = id === 'x' ? await api().getXConnectUrl(stockId) : await api().getLinkedInConnectUrl(stockId);
            window.location.href = url;
        } catch (e) {
            toast.error(errorText(e, `Couldn’t start the ${name} sign-in`));
        }
    };

    const chooseCompany = async (urn: string) => {
        if (!stockId || !urn) return;
        try {
            const l = await api().chooseLinkedInCompany(stockId, urn);
            showLinkedIn(l);
            toast.info(`LinkedIn posts will go to ${l.companyName}`);
        } catch (e) {
            toast.error(errorText(e, 'Couldn’t choose the LinkedIn company'));
        }
    };

    const disconnect = async (id: string) => {
        if (!stockId) return;
        const name = id === 'x' ? 'X' : 'LinkedIn';
        try {
            if (id === 'x') {
                await api().disconnectX(stockId);
                showX(null);
            } else {
                await api().disconnectLinkedIn(stockId);
                showLinkedIn(null);
            }
            toast.info(`${name} disconnected`);
        } catch (e) {
            toast.error(errorText(e, `Couldn’t disconnect ${name}`));
        }
    };

    return <section className="card">
        <div className="section-title">
            <span className="icon-tile"><FiShare2 /></span>
            <div className="text">
                <h2>Post Announcements to Social Media</h2>
                <div className="hint">Investi posts when the announcement email goes out.</div>
            </div>
        </div>

        <div className="accounts">
            {accounts.map(a => <div key={a.id} className={`account${a.connected ? '' : ' off'}`}>
                <span className="icon-tile small"><FiShare2 /></span>
                <div className="text"><strong>{a.name}</strong><span>{loaded ? a.detail : ''}</span></div>
                {!loaded && !(a.id === 'linkedin' && linkedInLoading) ? null
                    : a.connected
                    ? <>
                        <span className="badge ok">Connected</span>
                        <button type="button" className="btn-outline" onClick={() => disconnect(a.id)}>Disconnect</button>
                    </>
                    : a.id === 'linkedin' && linkedInLoading
                        ? <>
                            <FiLoader className="spin" />
                            <select className="company-select" aria-label="LinkedIn company" disabled><option>Loading companies…</option></select>
                        </>
                    : a.id === 'linkedin' && linkedInCompanies
                        ? <select className="company-select" aria-label="LinkedIn company" defaultValue="" onChange={e => chooseCompany(e.target.value)}>
                            <option value="" disabled>Choose a company</option>
                            {linkedInCompanies.map(p => <option key={p.urn} value={p.urn}>{p.name}</option>)}
                        </select>
                        : <button type="button" className="btn-outline with-icon" onClick={() => connect(a.id)}><FiPlus /> Connect</button>}
            </div>)}
        </div>

        <div className="setting-grid">
            {SETTINGS.map(s => <div key={s.key} className="setting-row">
                <div className="text">
                    <div className="title">{s.title}</div>
                    <div className="hint">{s.hint}</div>
                </div>
                <Toggle label={s.title} checked={form.social[s.key]} onChange={v => setForm(f => ({ ...f, social: { ...f.social, [s.key]: v } }))} />
            </div>)}
        </div>

        <div className="field">
            <label className="label" htmlFor="social-wording">What the post says <em>· the same wording is used on every account</em></label>
            <div className="insert-fields">
                <span>Click to add:</span>
                {fields.map(x => <button key={x.token} type="button" className="insert" title={`Adds ${x.token}`} onClick={() => insert(x.token)}><FiPlus /> {x.label}</button>)}
            </div>
            <textarea id="social-wording" ref={wordingRef} rows={7} value={form.social.wording} onChange={e => setWording(e.target.value)} />
        </div>
        <div className="preview"><span className="icon-tile small"><FiEye /></span><p>Preview: “{preview}”</p></div>


    </section>;
};

export default SocialPosts;
